import React, { useState, useRef, useEffect } from "react";
import { parsePillar, getStemColor_nobg, getHiddenStems, getBranchColor_nobg } from "../_helpers"
import { calculateBaziAnalysis, calculatePillarAnalysis } from "../bazi/_helper_bazi";
import { Loader, Clock, ArrowLeft, ArrowRight, Info, Heart, Star, Copy, Check } from "lucide-react";
import BZChart from "../bazi/BZChart";
import html2canvas from 'html2canvas';

/**
 * 八字详情组件 - 探索模式
 */
const ExploreDetailBazi = ({
  chartData,
  handleLike,
  handleFavorite,
  isLiked,
  isFavorited,
  likesCount,
  favoritesCount,
  interactionLoading
}) => {
  const baziRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // 大运流年相关状态
  const [loadingDaYun, setLoadingDaYun] = useState(false);
  const [daYunData, setDaYunData] = useState(null);
  const [daYunError, setDaYunError] = useState("");
  const [selectedDayunIndex, setSelectedDayunIndex] = useState(0);
  const [selectedLiunianIndex, setSelectedLiunianIndex] = useState(0);

  // 使用 BZChart.getFullBZChart 获取包含藏干的完整数据
  const fullChart = chartData ? BZChart.getFullBZChart(chartData) : null;

  // 当组件加载时直接加载大运流年数据
  useEffect(() => {
    if (chartData && !daYunData && !loadingDaYun) {
      loadDaYunData();
    }
  }, [chartData]);

  // 如果没有收到 chartData，显示错误
  if (!chartData) {
    return (
      <div className="p-6">
        <div className="mb-4 text-gray-700">未收到完整的命盘信息。</div>
      </div>
    );
  }

  if (!fullChart) {
    return (
      <div className="p-6">
        <div className="mb-4 text-red-600">命盘数据格式错误，无法解析。</div>
      </div>
    );
  }

  // 构造 result 和 birthData（兼容原有代码结构）
  const result = {
    year_pillar: fullChart.year_pillar,
    month_pillar: fullChart.month_pillar,
    day_pillar: fullChart.day_pillar,
    hour_pillar: fullChart.hour_pillar,
    year_pillar_hidden_stems: fullChart.year_pillar_hidden_stems,
    month_pillar_hidden_stems: fullChart.month_pillar_hidden_stems,
    day_pillar_hidden_stems: fullChart.day_pillar_hidden_stems,
    hour_pillar_hidden_stems: fullChart.hour_pillar_hidden_stems,
  };

  const birthData = {
    mingzhu_name: fullChart.mingzhu_name,
    gender: fullChart.mingzhu_gender,
    mingzhu_gender: fullChart.mingzhu_gender,
    year: fullChart.year,
    month: fullChart.month,
    day: fullChart.day,
    hour: fullChart.hour,
  };

  const baziAnalysis = calculateBaziAnalysis(birthData.year, birthData.month, birthData.day, birthData.hour);

  const displayName = fullChart.mingzhu_name || "";
  const displayGender = (() => {
    const g = fullChart.mingzhu_gender || "";
    if (!g) return "";
    const low = String(g).toLowerCase();
    if (["male", "m", "男", "man"].includes(low)) return "男";
    if (["female", "f", "女", "woman"].includes(low)) return "女";
    return g;
  })();

  const loadDaYunData = async () => {
    setLoadingDaYun(true);
    setDaYunError("");
    try {
      const data = await BZChart.calculateDaYun(chartData);
      setDaYunData(data);

      // 设置默认选中项
      if (data.daYunArr && data.daYunArr.length > 0) {
        // 找到当前大运的索引
        const currentIndex = data.daYunArr.findIndex(
          (p) => p === data.currentPeriod ||
            Number(p.period_index) === Number(data.currentPeriod?.period_index)
        );
        setSelectedDayunIndex(currentIndex !== -1 ? currentIndex : 0);

        // 找到当前流年的索引
        const selectedDayun = data.daYunArr[currentIndex !== -1 ? currentIndex : 0];
        const liunianList = Array.isArray(selectedDayun?.liunian) ? selectedDayun.liunian : [];
        if (data.liunianForCurrentYear && liunianList.length > 0) {
          const liIdx = liunianList.findIndex(
            (ln) => Number(ln.year) === Number(data.liunianForCurrentYear.year)
          );
          setSelectedLiunianIndex(liIdx !== -1 ? liIdx : 0);
        }
      }
    } catch (err) {
      console.error("加载大运流年失败:", err);
      setDaYunError(err.message || "加载大运流年数据失败");
    } finally {
      setLoadingDaYun(false);
    }
  };

  // 提取藏干的辅助函数
  const pickHidden = (obj) => {
    if (!obj) return [];
    return (
      getHiddenStems(obj.pillar) ||
      []
    );
  };

  const renderHiddenStems = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return null;
    return (
      <div className="mt-1 sm:mt-2">
        <div className="flex items-center justify-center gap-0.5 sm:gap-2 flex-wrap">
          {arr.map((h, idx) => (
            <span key={idx} className={`inline-block px-1 py-0.5 rounded text-[10px] sm:text-sm font-medium ${getStemColor_nobg(h)}`}>
              {h}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // 获取当前选中的大运和流年数据
  const getCurrentDaYunData = () => {
    if (!daYunData || !daYunData.daYunArr) return null;

    const currentPeriod = daYunData.daYunArr[selectedDayunIndex] || daYunData.currentPeriod;
    const currentPeriodHiddenStems = currentPeriod ? pickHidden(currentPeriod) : daYunData.currentPeriodHiddenStems || [];

    const liunianList = Array.isArray(currentPeriod?.liunian) ? currentPeriod.liunian : [];
    const liunianForCurrentYear = liunianList[selectedLiunianIndex] || daYunData.liunianForCurrentYear;
    const liunianCurrentYearHiddenStems = liunianForCurrentYear ? pickHidden(liunianForCurrentYear) : daYunData.liunianCurrentYearHiddenStems || [];

    return {
      currentPeriod,
      currentPeriodHiddenStems,
      liunianForCurrentYear,
      liunianCurrentYearHiddenStems,
    };
  };

  // 复制功能
  const handleCopy = async () => {
    if (!baziRef?.current) return;
    
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);

    try {
      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.top = "-9999px";
      container.style.left = "-9999px";
      const mobileWidth = 430; 
      container.style.width = `${mobileWidth}px`;
      container.style.backgroundColor = "#ffffff"; 
      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.padding = "16px";
      container.style.fontFamily = getComputedStyle(document.body).fontFamily;
      
      const contentClone = baziRef.current.cloneNode(true);
      contentClone.style.boxShadow = "none";
      contentClone.style.border = "none";
      contentClone.style.margin = "0";
      contentClone.style.width = "100%";
      contentClone.style.overflow = "visible";
      
      const scrollContainers = contentClone.querySelectorAll(".overflow-x-auto");
      scrollContainers.forEach(el => el.style.overflow = "visible");

      const buttons = contentClone.querySelectorAll("button");
      buttons.forEach(btn => btn.style.display = "none");

      container.appendChild(contentClone);
      document.body.appendChild(container);

      const scale = 3;
      const canvas = await html2canvas(container, {
        useCORS: true,
        scale: scale, 
        backgroundColor: "#ffffff"
      });
      
      document.body.removeChild(container);
      
      const footerHeight = 60;
      const scaledFooterHeight = footerHeight * scale; 
      
      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height + scaledFooterHeight;
      const ctx = finalCanvas.getContext("2d");
      
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      
      ctx.drawImage(canvas, 0, 0);
      
      ctx.fillStyle = "#f3f4f6";
      ctx.fillRect(0, canvas.height, finalCanvas.width, scaledFooterHeight);
      
      ctx.fillStyle = "#6b7280";
      ctx.font = `${12 * scale}px Arial, sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText("圆運命理系统", finalCanvas.width / 2, canvas.height + 20 * scale);
      ctx.fillText("bazi.tryonebest.com", finalCanvas.width / 2, canvas.height + 40 * scale);
      
      finalCanvas.toBlob(async (blob) => {
        if (blob) {
          try {
            const item = new ClipboardItem({ "image/png": blob });
            await navigator.clipboard.write([item]);
          } catch (clipboardError) {
            console.warn("Clipboard API failed, trying fallback", clipboardError);
            // Fallback: Download the image if clipboard copy fails
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "bazi-chart.png";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }
        }
      });
    } catch (error) {
      console.error("Copy failed:", error);
    }
  };

  return (
    <>
      <div className="w-full mx-auto px-0">
        {/* 合并的panel：基础信息 + 当前大运流年 + 全部大运列表 */}
        <div ref={baziRef} className="w-full p-0">
          {/* 基础信息部分 */}
          <div className="mb-2">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div>
                <p className="text-base sm:text-lg text-gray-700 font-medium">姓名：{displayName || "—"} &nbsp;|&nbsp; 性别：{displayGender || "—"}{fullChart.shudi ? ` 属地：${fullChart.shudi}` : ""}</p>

                   
                <p className="text-sm sm:text-base text-gray-600 mt-1">出生日期：{birthData.year}年{birthData.month}月{birthData.day}日 {birthData.hour}时</p>
                {(fullChart.zhanti_type || fullChart.zhanti) && (
                  <p className="text-sm sm:text-base text-gray-600 mt-1">
                    {fullChart.zhanti_type && `占题：${fullChart.zhanti_type}`}
                    {fullChart.zhanti_type && fullChart.zhanti && " | "}
                    {fullChart.zhanti && `${fullChart.zhanti}`}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* 水平分隔线 */}
          <hr className="border-gray-300 mb-2" />

          {/* 大运流年内容 */}
          {loadingDaYun && (
            <div className="p-6 text-center">
              <Loader className="animate-spin inline-block mr-2" />
              加载大运流年数据...
            </div>
          )}

          {daYunError && (
            <div className="p-6 text-red-600 bg-red-50 rounded-lg">
              {daYunError}
              <button
                onClick={loadDaYunData}
                className="ml-4 px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
              >
                重试
              </button>
            </div>
          )}

          {!loadingDaYun && !daYunError && daYunData && (
            <>
              {/* Panel E：起运信息 */}
              <div className="bg-white rounded-lg border border-gray-100 p-1.5 sm:p-2 mb-1 sm:mb-2">
                <div className="flex flex-col items-start justify-center p-1 sm:p-1.5">
                  <span className="text-xs text-gray-500 mb-1">起运：{daYunData.meta?.qiyun_des || "—"}</span>
                  <span className="text-xs text-gray-500 mb-1">折算：{daYunData.meta?.equivalent ?? "—"}</span>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:gap-3">
                {/* Panel F：当前流年 */}
                <div className="flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden p-2 sm:p-3 w-full min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">当前流年</h3>
                  <div className="w-full overflow-x-auto pb-1">
                    {(() => {
                      const current = getCurrentDaYunData();
                      const dayGan = result?.day_pillar ? parsePillar(result.day_pillar)[0] : null;

                    const pillars = [
                      { title: "大运", pillar: current?.currentPeriod?.pillar || current?.currentPeriod?.pillar_name, type: "dayun", color: "text-indigo-600" },
                      { title: "流年", pillar: current?.liunianForCurrentYear?.pillar, type: "liunian", color: "text-blue-600" },
                      { title: "年柱", pillar: result?.year_pillar, type: "year", color: "text-indigo-600" },
                      { title: "月柱", pillar: result?.month_pillar, type: "month", color: "text-purple-600" },
                      { title: "日柱", pillar: result?.day_pillar, type: "day", color: "text-pink-600" },
                      { title: "时柱", pillar: result?.hour_pillar, type: "hour", color: "text-blue-600" }
                    ];

                    // Prepare analysis data
                    const analysisData = pillars.map(p => {
                      if (!p.pillar) return null;
                      if (["year", "month", "day", "hour"].includes(p.type)) {
                        return baziAnalysis[p.type];
                      } else {
                        return calculatePillarAnalysis(dayGan, p.pillar);
                      }
                    });

                      return (
                        <div className="grid grid-cols-7 gap-0 text-center items-center w-full">
                        {/* Row 1: Title */}
                        <div className="text-[10px] sm:text-xs font-bold text-gray-500 pb-1">日期</div>
                        {pillars.map((p, i) => (
                          <div key={`t-${i}`} className={`text-[10px] sm:text-xs font-semibold ${p.color} pb-1`}>{p.title}</div>
                        ))}

                        {/* Row 2: Main Star */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">主星</div>
                        {analysisData.map((d, i) => (
                          <div key={`ms-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                            {d?.ganShiShen || ""}
                          </div>
                        ))}

                        {/* Row 3: Stem */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">天干</div>
                        {pillars.map((p, i) => {
                          const [stem] = parsePillar(p.pillar);
                          return (
                            <div key={`s-${i}`} className={`text-lg sm:text-xl font-bold ${stem ? getStemColor_nobg(stem) : ""}`}>
                              {stem || ""}
                            </div>
                          );
                        })}

                        {/* Row 4: Branch */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">地支</div>
                        {pillars.map((p, i) => {
                          const [, branch] = parsePillar(p.pillar);
                          return (
                            <div key={`b-${i}`} className={`text-lg sm:text-xl font-bold ${branch ? getBranchColor_nobg(branch) : ""}`}>
                              {branch || ""}
                            </div>
                          );
                        })}

                        {/* Row 5: Hidden Stems & Sub Star */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1 self-start mt-2">藏干</div>
                        {pillars.map((p, i) => {
                          const hidden = getHiddenStems(p.pillar);
                          const subStars = analysisData[i]?.zhiShiShen || [];
                          return (
                            <div key={`hs-${i}`} className="flex flex-col items-center gap-0 mt-2 self-start min-h-[50px]">
                              {/* 藏干在第一行 */}
                              <div className="flex items-center gap-1 flex-wrap mb-1">
                                {hidden && hidden.map((h, idx) => (
                                  <span key={`h-${idx}`} className={`text-xs font-medium leading-tight ${getStemColor_nobg(h)}`}>
                                    {h}
                                  </span>
                                ))}
                              </div>
                              {/* 副星在第二行 */}
                              <div className="flex items-center gap-1 flex-wrap">
                                {subStars.map((s, idx) => (
                                  <span key={`s-${idx}`} className="text-xs text-gray-600 leading-tight">
                                    {s}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {/* Row 6: Star Luck */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">星运</div>
                        {analysisData.map((d, i) => (
                          <div key={`sl-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                            {d?.starLuck || ""}
                          </div>
                        ))}

                        {/* Row 7: Self Sit */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">自坐</div>
                        {analysisData.map((d, i) => (
                          <div key={`sit-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                            {d?.diShi || ""}
                          </div>
                        ))}

                        {/* Row 8: Void */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">空亡</div>
                        {analysisData.map((d, i) => (
                          <div key={`kw-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                            {d?.xunKong || ""}
                          </div>
                        ))}

                        {/* Row 9: Na Yin */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">纳音</div>
                        {analysisData.map((d, i) => (
                          <div key={`ny-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                            {d?.naYin || ""}
                          </div>
                        ))}

                        {/* Row 10: Shen Sha */}
                        <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">神煞</div>
                        {pillars.map((_, i) => <div key={`sha-${i}`} className="py-1 min-h-[24px]"></div>)}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Panel G：大运列表 */}
                <div className="flex-1 bg-white rounded-lg border border-gray-200 overflow-hidden p-2 sm:p-3 w-full min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">大运列表</h3>

                {(!daYunData.daYunArr || daYunData.daYunArr.length === 0) && (
                  <div className="text-sm text-gray-500">未找到大运数据。</div>
                )}

                {daYunData.daYunArr && daYunData.daYunArr.length > 0 && (
                  <>
                    {/* 大运行 */}
                    <div className="w-full mb-0 pb-0 sm:mb-2 sm:pb-1">
                      <div className="grid grid-cols-10 w-full gap-0">
                        {Array.from({ length: 10 }).map((_, idx) => {
                          const p = daYunData.daYunArr[idx];
                          const isSelected = selectedDayunIndex === idx;
                          const startYear = p?.start_year || "";
                          const startAge = p?.start_age ?? "—";
                          const endAge = p?.end_age ?? "—";
                          return (
                            <div key={idx} className="w-full">
                              {p ? (
                                <button
                                  onClick={() => {
                                    setSelectedDayunIndex(idx);
                                    setSelectedLiunianIndex(0);
                                  }}
                                  className={`w-full flex flex-col text-center py-1 sm:py-2 box-border ${isSelected ? "bg-indigo-100 text-indigo-800" : "text-gray-800"}`}
                                >
                                  {/* 起始年份 - 固定在顶部 */}
                                  <div className="text-xs mb-0 sm:mb-1 flex-shrink-0">{startYear}</div>

                                  {/* 天干地支 */}
                                  <div className="flex-1 flex flex-col items-center justify-start pt-1 sm:pt-2 overflow-hidden">
                                    {(() => {
                                      const [stem, branch] = parsePillar(p.pillar);
                                      return (
                                        <>
                                          <div className={`text-base sm:text-lg font-bold leading-tight ${stem ? getStemColor_nobg(stem) : "text-gray-400"}`}>{stem || "—"}</div>
                                          <div className={`text-base sm:text-lg font-bold leading-tight ${branch ? getBranchColor_nobg(branch) : "text-gray-400"}`}>{branch || "—"}</div>
                                        </>
                                      );
                                    })()}
                                  </div>

                                  {/* 年龄 */}
                                  <div className="text-xs mt-1 sm:mt-2 flex-shrink-0">
                                    <div>{startAge} 岁</div>
                                    <div className="text-gray-400 mt-0.5">{endAge} 岁</div>
                                  </div>
                                </button>
                              ) : (
                                <div
                                  className="w-full rounded-lg box-border flex items-center justify-center text-gray-300"
                                >
                                  —
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* 流年行 */}
                    <div className="w-full pb-0 sm:pb-1">
                      <div className="grid grid-cols-10 w-full gap-0">
                        {(() => {
                          const selDayun = daYunData.daYunArr[selectedDayunIndex] || {};
                          const liList = Array.isArray(selDayun.liunian) ? selDayun.liunian : [];
                          return Array.from({ length: 10 }).map((_, i) => {
                            const ln = liList[i];
                            const isLiSelected = selectedLiunianIndex === i;
                            return (
                              <div key={i} className="w-full">
                                {ln ? (
                                  <button
                                    onClick={() => setSelectedLiunianIndex(i)}
                                    className={`w-full flex flex-col text-center py-1 sm:py-2 box-border ${isLiSelected ? "bg-indigo-100 text-indigo-800" : "text-gray-800"}`}
                                  >
                                    {/* 年份 - 固定在顶部 */}
                                    <div className="text-xs mb-0 sm:mb-1 flex-shrink-0">{ln.year}</div>

                                    {/* 天干地支 */}
                                    <div className="flex-1 flex flex-col items-center justify-start pt-1 sm:pt-2 overflow-hidden">
                                      {(() => {
                                        const [stem, branch] = parsePillar(ln.pillar);
                                        return (
                                          <>
                                            <div className={`text-base sm:text-lg font-bold leading-tight ${stem ? getStemColor_nobg(stem) : "text-gray-400"}`}>{stem || "—"}</div>
                                            <div className={`text-base sm:text-lg font-bold leading-tight ${branch ? getBranchColor_nobg(branch) : "text-gray-400"}`}>{branch || "—"}</div>
                                          </>
                                        );
                                      })()}
                                    </div>

                                    {/* 年龄 */}
                                    <div className="text-xs mt-1 sm:mt-2 text-gray-500">
                                      {ln.age ?? ""}
                                    </div>
                                  </button>
                                ) : (
                                  <div
                                    className="w-full rounded-lg box-border flex items-center justify-center text-gray-300"
                                  >
                                    —
                                  </div>
                                )}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </>
                )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
        {/* 点赞和收藏按钮 */}
        <div className="flex items-center justify-start gap-4 py-2">
          <button
            onClick={handleLike}
            disabled={interactionLoading}
            className={`flex items-center gap-1 transition-all ${
              isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'
            } ${interactionLoading ? 'opacity-50' : ''}`}
            title={isLiked ? '取消点赞' : '点赞'}
          >
            <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
            <span className="text-xs font-medium">{likesCount}</span>
          </button>
          <button
            onClick={handleFavorite}
            disabled={interactionLoading}
            className={`flex items-center gap-1 transition-all ${
              isFavorited ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-400'
            } ${interactionLoading ? 'opacity-50' : ''}`}
            title={isFavorited ? '取消收藏' : '收藏'}
          >
            <Star className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
            <span className="text-xs font-medium">{favoritesCount}</span>
          </button>
        </div>

        {/* 复制按钮 */}
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 bg-gray-50 hover:bg-indigo-50 rounded-md transition-colors border border-transparent hover:border-indigo-100"
          title="复制排盘结果为图片"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "已复制" : "复制结果"}
        </button>
      </div>
    </>
  );
};

export default ExploreDetailBazi;